import React, { useState } from 'react';
import type { TeleportContextValue } from '@/hooks/useTeleportToTarget';
import {
  CHAT_PART_ATTACHED_PLANNER,
  CHAT_PART_ATTACHED_PROJECT,
  CHAT_PART_ATTACHED_SESSION,
  type ChatAttachedProjectPayload,
  type ChatAttachedSessionPayload,
  type TrackerChatMessage,
} from '@/components/Messenger/types/chatMessageTypes';
import { ContextAttachmentChips, type MessengerContextAttachment } from './ContextAttachmentChips';
import { MessageMeta } from './MessageMeta';
import { MessageTextWithMeta } from './MessageTextWithMeta';
import { BubbleShell } from './BubbleShell';
import { DmMessageBubble } from './DmMessageBubble';
import { resolveBubbleMode, type BubbleMode } from './bubbleMode';
import { MessengerInlineImage } from '../attachments/MessengerInlineImage';
import { MessengerDmFileAttachment } from '../attachments/MessengerDmFileAttachment';
import { isMessengerImageMime } from '../attachments/messengerImageMime';
import { linkifyPlainText } from '@/utils/linkifyPlainText';

type UserMessagePart = TrackerChatMessage['parts'][number];

function partIsAttachment(p: UserMessagePart): boolean {
  return (
    p.type === CHAT_PART_ATTACHED_PROJECT ||
    p.type === CHAT_PART_ATTACHED_SESSION ||
    p.type === CHAT_PART_ATTACHED_PLANNER
  );
}

function partIsImageFile(p: UserMessagePart): boolean {
  return Boolean(
    p.type === 'file' &&
      'mediaType' in p &&
      isMessengerImageMime(p.mediaType) &&
      'url' in p &&
      p.url,
  );
}

function countImageParts(parts: readonly UserMessagePart[]): number {
  return parts.filter(partIsImageFile).length;
}

function hasNonemptyTextInParts(parts: readonly UserMessagePart[]): boolean {
  return parts.some(
    (p) => p.type === 'text' && 'text' in p && typeof p.text === 'string' && p.text.length > 0,
  );
}

function indexOfLastNonemptyText(parts: readonly UserMessagePart[]): number {
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    if (p.type === 'text' && 'text' in p && typeof p.text === 'string' && p.text.length > 0) {
      return i;
    }
  }
  return -1;
}

function hasNonTextAfter(parts: readonly UserMessagePart[], startExclusive: number): boolean {
  for (let i = startExclusive + 1; i < parts.length; i++) {
    const p = parts[i];
    if (partIsAttachment(p) || p.type === 'file') return true;
  }
  return false;
}

function hasAnyNonTextAttachment(parts: readonly UserMessagePart[]): boolean {
  return parts.some((p) => partIsAttachment(p) || p.type === 'file');
}

function buildChatUserMessageSegments(
  parts: readonly UserMessagePart[],
  teleport: TeleportContextValue | null,
  timeLabel: string | undefined,
  ctx: {
    imageCount: number;
    mode: BubbleMode;
    hasFooter: boolean;
    iLastText: number;
    onImageSlotResize?: (w: number) => void;
    /** Агентский чат: без времени и слота меты в пузыре */
    suppressMeta?: boolean;
  },
): React.ReactNode[] {
  const { imageCount, mode, hasFooter, iLastText, onImageSlotResize, suppressMeta } = ctx;
  const contentSegments: React.ReactNode[] = [];
  let seenImageParts = 0;
  let i = 0;

  while (i < parts.length) {
    const p = parts[i];

    if (partIsAttachment(p)) {
      const start = i;
      const batch: MessengerContextAttachment[] = [];
      while (i < parts.length) {
        const q = parts[i];
        if (q.type === CHAT_PART_ATTACHED_PROJECT) {
          const d = q.data as ChatAttachedProjectPayload;
          if (d?.id) batch.push({ type: 'project', id: d.id, name: d.name });
          i++;
          continue;
        }
        if (q.type === CHAT_PART_ATTACHED_SESSION) {
          const d = q.data as ChatAttachedSessionPayload;
          if (d?.id && d?.date) {
            batch.push({ type: 'session', id: d.id, date: d.date, label: d.label || d.date });
          }
          i++;
          continue;
        }
        if (q.type === CHAT_PART_ATTACHED_PLANNER) {
          batch.push({ type: 'planner' });
          i++;
          continue;
        }
        break;
      }
      if (batch.length > 0) {
        contentSegments.push(
          <ContextAttachmentChips key={`ar-${start}`} attachments={batch} teleport={teleport} />,
        );
      }
      continue;
    }

    if (partIsImageFile(p)) {
      seenImageParts++;
      const isLastImage = seenImageParts === imageCount;
      const url = 'url' in p ? (p.url as string) : '';
      const filename = 'filename' in p ? (p.filename ?? undefined) : undefined;

      contentSegments.push(
        <div key={i} className="chatAssistant__userMsgImageBlock">
          <MessengerInlineImage
            src={url}
            alt={filename}
            onSlotResize={mode === 'textBubble' ? onImageSlotResize : undefined}
          />
          {/* imageOnlyBubble: мета в углу последней картинки */}
          {mode === 'imageOnlyBubble' && isLastImage && !suppressMeta ? (
            <MessageMeta variant="corner" timeLabel={timeLabel} isOwn />
          ) : null}
        </div>,
      );
      i++;
      continue;
    }

    if (p.type === 'file' && 'url' in p && p.url && !partIsImageFile(p)) {
      const url = p.url as string;
      const filename = 'filename' in p ? (p.filename ?? undefined) : undefined;
      contentSegments.push(
        <div key={i} className="chatAssistant__dmAttachmentBlock">
          <MessengerDmFileAttachment href={url} fileName={filename} isOwn />
        </div>,
      );
      i++;
      continue;
    }

    if (p.type === 'text' && 'text' in p && typeof p.text === 'string') {
      const t = p.text;
      if (t.length > 0) {
        const isTailText =
          i === iLastText && !hasNonTextAfter(parts, i) && mode === 'textBubble' && !hasFooter;

        contentSegments.push(
          isTailText && !suppressMeta ? (
            <MessageTextWithMeta
              key={i}
              text={t}
              bodyClassName="chatAssistant__userMessageText"
              meta={<MessageMeta timeLabel={timeLabel} isOwn inline />}
            />
          ) : (
            <div key={i} className="chatAssistant__userMessageText">
              {linkifyPlainText(t)}
            </div>
          ),
        );
      }
      i++;
      continue;
    }

    i++;
  }

  return contentSegments;
}

function ChatUserMessageTextBubbleWithShell({
  parts,
  teleport,
  timeLabel,
  imageCount,
  mode,
  hasFooter,
  iLastText,
  hasImage,
  suppressMeta,
}: {
  parts: readonly UserMessagePart[];
  teleport: TeleportContextValue | null;
  timeLabel?: string;
  imageCount: number;
  mode: BubbleMode;
  hasFooter: boolean;
  iLastText: number;
  hasImage: boolean;
  suppressMeta?: boolean;
}) {
  const [imageSlotWidth, setImageSlotWidth] = useState<number | null>(null);
  const contentSegments = buildChatUserMessageSegments(parts, teleport, timeLabel, {
    imageCount,
    mode,
    hasFooter,
    iLastText,
    onImageSlotResize: (w) => setImageSlotWidth(w),
    suppressMeta,
  });

  return (
    <DmMessageBubble variant="own">
      <BubbleShell
        content={<>{contentSegments}</>}
        slotWidth={hasImage ? imageSlotWidth : null}
      />
    </DmMessageBubble>
  );
}

export function ChatUserMessageContent({
  parts,
  teleport,
  timeLabel,
  suppressMeta = false,
}: {
  parts: readonly UserMessagePart[];
  teleport: TeleportContextValue | null;
  timeLabel?: string;
  /** Чат с Астрой: не показывать время и блок меты */
  suppressMeta?: boolean;
}): React.ReactNode {
  const imageCount = countImageParts(parts);
  const hasText = hasNonemptyTextInParts(parts);
  const hasImage = imageCount > 0;
  const hasAttachment = hasAnyNonTextAttachment(parts);

  const mode = resolveBubbleMode({ hasText, hasImage, hasAttachment });

  // Для agentChat лайков нет — footer всегда отсутствует
  const hasFooter = false;

  const iLastText = indexOfLastNonemptyText(parts);

  const contentSegments = buildChatUserMessageSegments(parts, teleport, timeLabel, {
    imageCount,
    mode,
    hasFooter,
    iLastText,
    suppressMeta,
  });

  if (contentSegments.length === 0) return null;

  // ── attachmentOnlyBubble: мета внутри блока карточек ─────────────────────

  if (mode === 'attachmentOnlyBubble') {
    return (
      <DmMessageBubble variant="own" surface="attachmentBare">
        {suppressMeta ? (
          contentSegments
        ) : (
          <div className="chatAssistant__dmAttachmentMetaHost">
            {contentSegments}
            <MessageMeta variant="corner" timeLabel={timeLabel} isOwn />
          </div>
        )}
      </DmMessageBubble>
    );
  }

  // ── imageOnlyBubble: просто контент (мета уже внутри imageBlock) ──────────

  if (mode === 'imageOnlyBubble') {
    return <DmMessageBubble variant="own">{contentSegments}</DmMessageBubble>;
  }

  // ── textBubble ────────────────────────────────────────────────────────────

  return (
    <ChatUserMessageTextBubbleWithShell
      parts={parts}
      teleport={teleport}
      timeLabel={timeLabel}
      imageCount={imageCount}
      mode={mode}
      hasFooter={hasFooter}
      iLastText={iLastText}
      hasImage={hasImage}
      suppressMeta={suppressMeta}
    />
  );
}
